import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { inventoryApi } from '../api/inventory-api-instance';
import { AppliedFilters } from '../components/AppliedFilters';
import { ErrorState } from '../components/ErrorState';
import { PageControls } from '../components/PageControls';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { SearchableSelect, type SearchableSelectOption } from '../components/SearchableSelect';
import { SortableTableHeader } from '../components/SortableTableHeader';
import type { Customer } from '../domain/inventory';
import {
  cameraSystemCountsByCustomer,
  compareCameraSystems,
  customerLocationSummary,
  emptyCameraSystemCounts,
  type CameraSystemCounts,
} from '../features/customers/customer-insights';
import {
  FULL_FLEET_ITEMS_QUERY_KEY,
  FULL_FLEET_PAGE_SIZE,
} from '../features/dashboard/aggregations';
import { humanizeIdentifier, sortByDisplayLabel } from '../lib/formatting';

const CUSTOMERS_PAGE_SIZE = 20;
const customerSortKeys = [
  'customer',
  'active',
  'assigned',
  'ordered',
  'country',
  'type',
  'region',
  'state',
] as const;
// Camera system columns sort biggest first on the first click.
const numericSortKeys = new Set<CustomerSortKey>(['active', 'assigned', 'ordered']);
type CustomerSortKey = (typeof customerSortKeys)[number];

function isCustomerSortKey(value: string | null): value is CustomerSortKey {
  return customerSortKeys.some((key) => key === value);
}

function customerType(customer: Customer): string {
  // CUSTOMER is the protobuf enum's zero value, so it is intentionally omitted
  // from JSON responses even though it is the customer's actual type.
  return customer.customerType ?? 'CUSTOMER';
}

function countryLabel(country: string): string {
  if (!country) return 'Not recorded';
  if (!/^[a-z]{2}$/i.test(country)) return country;
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(country.toUpperCase()) ?? country;
  } catch {
    return country;
  }
}

function updateSearchParam(
  params: URLSearchParams,
  setParams: ReturnType<typeof useSearchParams>[1],
  name: string,
  value: string,
): void {
  const next = new URLSearchParams(params);
  if (value) next.set(name, value);
  else next.delete(name);
  next.delete('page');
  setParams(next);
}

export function CustomersPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const typeFilter = params.get('type') ?? '';
  const stateFilter = params.get('state') ?? '';
  const countryFilter = params.get('country') ?? '';
  const regionFilter = params.get('region') ?? '';
  const sortParam = params.get('sort');
  const sortKey = isCustomerSortKey(sortParam) ? sortParam : null;
  const sortDirection = params.get('dir') === 'desc' ? 'desc' : 'asc';
  const requestedPage = Math.max(1, Number(params.get('page') ?? 1) || 1);

  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => inventoryApi.listCustomers(),
  });
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => inventoryApi.listSites() });
  // The full-fleet items are already cached by AppShell.
  const items = useQuery({
    queryKey: FULL_FLEET_ITEMS_QUERY_KEY,
    queryFn: () => inventoryApi.listItems({ pageSize: FULL_FLEET_PAGE_SIZE }),
  });
  const parts = useQuery({ queryKey: ['parts'], queryFn: () => inventoryApi.listParts() });
  const camerasByCustomer = useMemo(
    () => cameraSystemCountsByCustomer(sites.data ?? [], items.data?.data ?? [], parts.data ?? []),
    [items.data, parts.data, sites.data],
  );
  const locationsByCustomer = useMemo(() => {
    const map = new Map<string, ReturnType<typeof customerLocationSummary>>();
    for (const customer of customers.data ?? []) {
      map.set(customer.id, customerLocationSummary(customer.id, sites.data ?? []));
    }
    return map;
  }, [customers.data, sites.data]);

  const typeOptions = useMemo<SearchableSelectOption[]>(() => {
    const types = new Set((customers.data ?? []).map(customerType));
    return [
      { value: '', label: 'All types' },
      ...sortByDisplayLabel([...types], humanizeIdentifier).map((type) => ({
        value: type,
        label: humanizeIdentifier(type),
      })),
    ];
  }, [customers.data]);

  const stateOptions = useMemo<SearchableSelectOption[]>(() => {
    const states = new Set((customers.data ?? []).map((customer) => customer.state));
    return [
      { value: '', label: 'All states' },
      ...sortByDisplayLabel([...states], humanizeIdentifier).map((state) => ({
        value: state,
        label: humanizeIdentifier(state),
      })),
    ];
  }, [customers.data]);

  const countryOptions = useMemo<SearchableSelectOption[]>(() => {
    const countries = new Set<string>();
    for (const location of locationsByCustomer.values()) {
      for (const country of location.countries) countries.add(country);
    }
    return [
      { value: '', label: 'All countries' },
      ...sortByDisplayLabel([...countries], countryLabel).map((country) => ({
        value: country,
        label: countryLabel(country),
      })),
    ];
  }, [locationsByCustomer]);

  const regionOptions = useMemo<SearchableSelectOption[]>(() => {
    const regions = new Set<string>();
    for (const locationSummary of locationsByCustomer.values()) {
      for (const location of locationSummary.locations) {
        if (!countryFilter || location.country === countryFilter) {
          if (location.region) regions.add(location.region);
        }
      }
    }
    return [
      { value: '', label: 'All regions' },
      ...sortByDisplayLabel([...regions], (region) => region).map((region) => ({
        value: region,
        label: region,
      })),
    ];
  }, [countryFilter, locationsByCustomer]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matchingCustomers = (customers.data ?? []).filter((customer) => {
      const location = locationsByCustomer.get(customer.id);
      const locations = location?.locations ?? [];
      return (
        (!needle ||
          [
            customer.id,
            customer.name,
            customer.displayName ?? '',
            ...(location?.countries ?? []),
            ...(location?.regions ?? []),
          ]
            .join(' ')
            .toLowerCase()
            .includes(needle)) &&
        (!typeFilter || customerType(customer) === typeFilter) &&
        (!stateFilter || customer.state === stateFilter) &&
        (!countryFilter || locations.some((entry) => entry.country === countryFilter)) &&
        (!regionFilter ||
          locations.some(
            (entry) =>
              entry.region === regionFilter && (!countryFilter || entry.country === countryFilter),
          ))
      );
    });

    const cameras = (customer: Customer) =>
      camerasByCustomer.get(customer.id) ?? emptyCameraSystemCounts();
    return matchingCustomers.sort((left, right) => {
      // By default the biggest customers come first: most active camera
      // systems, then assigned, then ordered.
      if (!sortKey)
        return (
          compareCameraSystems(cameras(left), cameras(right)) ||
          (left.displayName ?? left.name).localeCompare(right.displayName ?? right.name)
        );
      const comparison = compareCustomers(
        left,
        right,
        sortKey,
        locationsByCustomer,
        camerasByCustomer,
      );
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [
    countryFilter,
    customers.data,
    locationsByCustomer,
    camerasByCustomer,
    query,
    regionFilter,
    stateFilter,
    sortDirection,
    sortKey,
    typeFilter,
  ]);

  const directorySummary = useMemo(() => {
    const allCustomers = customers.data ?? [];
    const allRegions = new Set<string>();
    for (const site of sites.data ?? []) {
      const region = site.labels?.region?.trim();
      if (region) allRegions.add(region);
    }
    return {
      total: allCustomers.length,
      active: allCustomers.filter((customer) => customer.state === 'ACTIVE').length,
      customerAccounts: allCustomers.filter((customer) => customerType(customer) === 'CUSTOMER')
        .length,
      regions: allRegions.size,
    };
  }, [customers.data, sites.data]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / CUSTOMERS_PAGE_SIZE));
  const page = Math.min(requestedPage, pageCount);
  const visibleCustomers = filtered.slice(
    (page - 1) * CUSTOMERS_PAGE_SIZE,
    page * CUSTOMERS_PAGE_SIZE,
  );

  function setPage(nextPage: number): void {
    const next = new URLSearchParams(params);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setParams(next);
  }

  function setCountryFilter(value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set('country', value);
    else next.delete('country');
    next.delete('region');
    next.delete('page');
    setParams(next);
  }

  function updateSort(key: CustomerSortKey): void {
    const next = new URLSearchParams(params);
    if (sortKey === key) {
      if (sortDirection === 'asc') next.set('dir', 'desc');
      else {
        next.delete('sort');
        next.delete('dir');
      }
    } else {
      next.set('sort', key);
      next.set('dir', numericSortKeys.has(key) ? 'desc' : 'asc');
    }
    next.delete('page');
    setParams(next);
  }

  if (customers.isLoading || sites.isLoading) return <PageLoadingState label="Loading customers" />;
  if (customers.isError || sites.isError) {
    return (
      <ErrorState
        title="Customers could not be loaded"
        rpcName="ListCustomers"
        onRetry={() => {
          void customers.refetch();
          void sites.refetch();
        }}
      />
    );
  }

  const activeFilters = [
    query.trim()
      ? {
          key: 'q',
          label: `Search: ${query.trim()}`,
          onRemove: () => updateSearchParam(params, setParams, 'q', ''),
        }
      : null,
    countryFilter
      ? {
          key: 'country',
          label: `Country: ${countryLabel(countryFilter)}`,
          onRemove: () => setCountryFilter(''),
        }
      : null,
    typeFilter
      ? {
          key: 'type',
          label: `Type: ${humanizeIdentifier(typeFilter)}`,
          onRemove: () => updateSearchParam(params, setParams, 'type', ''),
        }
      : null,
    regionFilter
      ? {
          key: 'region',
          label: `Region: ${regionFilter}`,
          onRemove: () => updateSearchParam(params, setParams, 'region', ''),
        }
      : null,
    stateFilter
      ? {
          key: 'state',
          label: `State: ${humanizeIdentifier(stateFilter)}`,
          onRemove: () => updateSearchParam(params, setParams, 'state', ''),
        }
      : null,
    sortKey
      ? {
          key: 'sort',
          label: `Sort: ${customerSortLabel(sortKey)} (${sortDirection})`,
          onRemove: () => {
            const next = new URLSearchParams(params);
            next.delete('sort');
            next.delete('dir');
            setParams(next);
          },
        }
      : null,
  ].filter(
    (filter): filter is { key: string; label: string; onRemove: () => void } => filter !== null,
  );

  return (
    <>
      <PageHeader
        title="Customers"
        description="Customer accounts, locations, and operational status."
      />

      <div className="compact-metrics customer-directory-metrics" aria-label="Customer summary">
        <div>
          <span>Accounts</span>
          <strong>{directorySummary.total.toLocaleString()}</strong>
        </div>
        <div>
          <span>Active</span>
          <strong>{directorySummary.active.toLocaleString()}</strong>
        </div>
        <div>
          <span>Customers</span>
          <strong>{directorySummary.customerAccounts.toLocaleString()}</strong>
        </div>
        <div>
          <span>Regions</span>
          <strong>{directorySummary.regions.toLocaleString()}</strong>
        </div>
      </div>

      <section className="reference-workspace" aria-label="Customer directory">
        <form className="customer-directory-controls" role="search" aria-label="Filter customers">
          <label className="customer-directory-search">
            <span>Search</span>
            <input
              type="search"
              value={query}
              onChange={(event) => updateSearchParam(params, setParams, 'q', event.target.value)}
              placeholder="Customer name, ID, country, or region"
              aria-label="Search customers"
            />
          </label>
          <label>
            <span>Country</span>
            <SearchableSelect
              ariaLabel="Customer country"
              value={countryFilter}
              onChange={setCountryFilter}
              options={countryOptions}
              placeholder="All countries"
            />
          </label>
          <label>
            <span>Type</span>
            <SearchableSelect
              ariaLabel="Customer type"
              value={typeFilter}
              onChange={(value) => updateSearchParam(params, setParams, 'type', value)}
              options={typeOptions}
              placeholder="All types"
            />
          </label>
          <label>
            <span>Region</span>
            <SearchableSelect
              ariaLabel="Customer region"
              value={regionFilter}
              onChange={(value) => updateSearchParam(params, setParams, 'region', value)}
              options={regionOptions}
              placeholder="All regions"
            />
          </label>
          <label>
            <span>State</span>
            <SearchableSelect
              ariaLabel="Customer state"
              value={stateFilter}
              onChange={(value) => updateSearchParam(params, setParams, 'state', value)}
              options={stateOptions}
              placeholder="All states"
            />
          </label>
        </form>
        <AppliedFilters filters={activeFilters} />

        <div className="table-wrap customer-directory-table">
          <table>
            <thead>
              <tr>
                <SortableTableHeader
                  label="Customer"
                  sortKey="customer"
                  activeKey={sortKey}
                  direction={sortDirection}
                  onSort={(key) => updateSort(key as CustomerSortKey)}
                />
                <SortableTableHeader
                  label="Active"
                  sortKey="active"
                  className="numeric-cell customer-count-column"
                  activeKey={sortKey}
                  direction={sortDirection}
                  onSort={(key) => updateSort(key as CustomerSortKey)}
                />
                <SortableTableHeader
                  label="Assigned"
                  sortKey="assigned"
                  className="numeric-cell customer-count-column"
                  activeKey={sortKey}
                  direction={sortDirection}
                  onSort={(key) => updateSort(key as CustomerSortKey)}
                />
                <SortableTableHeader
                  label="Ordered"
                  sortKey="ordered"
                  className="numeric-cell customer-count-column"
                  activeKey={sortKey}
                  direction={sortDirection}
                  onSort={(key) => updateSort(key as CustomerSortKey)}
                />
                <SortableTableHeader
                  label="Country"
                  sortKey="country"
                  activeKey={sortKey}
                  direction={sortDirection}
                  onSort={(key) => updateSort(key as CustomerSortKey)}
                />
                <SortableTableHeader
                  label="Type"
                  sortKey="type"
                  activeKey={sortKey}
                  direction={sortDirection}
                  onSort={(key) => updateSort(key as CustomerSortKey)}
                />
                <SortableTableHeader
                  label="Region"
                  sortKey="region"
                  activeKey={sortKey}
                  direction={sortDirection}
                  onSort={(key) => updateSort(key as CustomerSortKey)}
                />
                <SortableTableHeader
                  label="State"
                  sortKey="state"
                  activeKey={sortKey}
                  direction={sortDirection}
                  onSort={(key) => updateSort(key as CustomerSortKey)}
                />
              </tr>
            </thead>
            <tbody>
              {visibleCustomers.map((customer) => {
                const displayName = customer.displayName ?? customer.name;
                const location = locationsByCustomer.get(customer.id);
                const cameras = camerasByCustomer.get(customer.id) ?? emptyCameraSystemCounts();
                return (
                  <tr key={customer.id}>
                    <td className="customer-name-cell">
                      <Link to={`/customers/${encodeURIComponent(customer.id)}`}>
                        {displayName}
                      </Link>
                      {customer.displayName && customer.name !== customer.displayName ? (
                        <small>{customer.name}</small>
                      ) : null}
                    </td>
                    <td className="numeric-cell">{cameras.active.toLocaleString()}</td>
                    <td className="numeric-cell">{cameras.assigned.toLocaleString()}</td>
                    <td className="numeric-cell">{cameras.ordered.toLocaleString()}</td>
                    <td>
                      {location?.countries.length
                        ? location.countries.map(countryLabel).join(', ')
                        : 'Not recorded'}
                    </td>
                    <td>{humanizeIdentifier(customerType(customer))}</td>
                    <td>
                      {location?.regions.length ? location.regions.join(', ') : 'Not recorded'}
                    </td>
                    <td>
                      <span className="customer-state">
                        <span aria-hidden="true" />
                        {humanizeIdentifier(customer.state)}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 ? (
                <tr>
                  <td className="customer-directory-empty" colSpan={8}>
                    <strong>No customers match these filters.</strong>
                    <button type="button" onClick={() => setParams(new URLSearchParams())}>
                      Clear filters
                    </button>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 ? (
          <PageControls
            page={page}
            pageSize={CUSTOMERS_PAGE_SIZE}
            total={filtered.length}
            onPageChange={setPage}
            itemLabel="customers"
          />
        ) : null}
      </section>
    </>
  );
}

function compareCustomers(
  left: Customer,
  right: Customer,
  sortKey: CustomerSortKey,
  locationsByCustomer: ReadonlyMap<string, ReturnType<typeof customerLocationSummary>>,
  camerasByCustomer: ReadonlyMap<string, CameraSystemCounts>,
): number {
  if (sortKey === 'active' || sortKey === 'assigned' || sortKey === 'ordered') {
    const value = (customer: Customer) => camerasByCustomer.get(customer.id)?.[sortKey] ?? 0;
    return (
      value(left) - value(right) ||
      (left.displayName ?? left.name).localeCompare(right.displayName ?? right.name)
    );
  }
  const leftLocation = locationsByCustomer.get(left.id);
  const rightLocation = locationsByCustomer.get(right.id);
  const leftValues = {
    customer: left.displayName ?? left.name,
    country: leftLocation?.countries.map(countryLabel).join(', ') ?? '',
    type: humanizeIdentifier(customerType(left)),
    region: leftLocation?.regions.join(', ') ?? '',
    state: humanizeIdentifier(left.state),
  };
  const rightValues = {
    customer: right.displayName ?? right.name,
    country: rightLocation?.countries.map(countryLabel).join(', ') ?? '',
    type: humanizeIdentifier(customerType(right)),
    region: rightLocation?.regions.join(', ') ?? '',
    state: humanizeIdentifier(right.state),
  };
  return leftValues[sortKey].localeCompare(rightValues[sortKey]);
}

function customerSortLabel(key: CustomerSortKey): string {
  return {
    customer: 'Customer',
    active: 'Active camera systems',
    assigned: 'Assigned camera systems',
    ordered: 'Ordered camera systems',
    country: 'Country',
    type: 'Type',
    region: 'Region',
    state: 'State',
  }[key];
}
